import { selectUserInfo } from "features/user/userSlice";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { InsuranceProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import AssuredWorkloadOutlinedIcon from "@mui/icons-material/AssuredWorkloadOutlined";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import CommonCheckbox from "common/CheckBox/Checkbox";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GetInsuranceCompanyByIdResponse } from "subModule/src/services/insurance/interface";
import BasicDatePicker from "common/Pickers/Pickers";
import { useGetPriceListDropdownService } from "subModule/src/services/pricelist/usePriceList";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useGetRoleDropdownService } from "subModule/src/services/role/useRole";
import InsuranceTabs from "./InsuranceTabs";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}
export default function InsuranceCompaniesModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [username, setUsername] = useState<string>("");
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      invoicingPreference: { value: 1, label: t("Individual") },
    },
  });
  const { data: priceListData, refetch: priceListRefetch } =
    useGetPriceListDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: rolesData, refetch: rolesRefetch } = useGetRoleDropdownService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm,
  });
  const priceLists = (priceListData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const roles = (rolesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedInsuranceCompanyPlaceholder = (lang?: string) =>
    isArabicLanguage(lang)
      ? t("phInsuranceCompanyAr")
      : t("phInsuranceCompanyEn");
  const localizedContactPersonPlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phContactPersonAr") : t("phContactPersonEn");
  const companyId =
    params?.id && params.id !== "create" ? params.id : undefined;
  const [loading, setLoading] = useState<boolean>(!!companyId);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(!companyId);
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    moduleLabelKey: "insuranceCompanies",
    moduleHref: "/insurance-companies",
    entityFallbackLabelKey: "title",
    isEdit: !!companyId,
  });
  const invoicingPreferences = [
    { value: 1, label: t("Individual") },
    { value: 2, label: t("consolidated") },
  ];
  useEffect(() => {
    priceListRefetch();
    rolesRefetch();
    if (companyId) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && companyId) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    InsuranceProfileService.getInsuranceCompanyByID(+companyId!)
      .then((res: GetInsuranceCompanyByIdResponse) => {
        if (res.success) {
          reset({
            ...(res?.data || {}),
            priceListId: res.data?.priceListId
              ? {
                  value: res.data?.priceListId,
                  label: res?.data?.priceListTitle || res.data?.priceListId,
                }
              : null,
            invoicingPreference: res.data?.invoicingPreference
              ? {
                  value: res.data?.invoicingPreference,
                  label:
                    invoicingPreferences[
                      invoicingPreferences.findIndex(
                        (v) => v.value === res.data?.invoicingPreference,
                      )
                    ]?.label || "",
                }
              : { value: 1, label: t("Individual") },
            addressesRequest: res?.data?.addressesResponse
              ? {
                  ...res?.data?.addressesResponse,
                  address: res?.data?.addressesResponse?.address || "",
                }
              : null,
            insuranceUserRequest: res?.data?.insuranceUserResponse
              ? {
                  ...res?.data?.insuranceUserResponse,
                  roleId: {
                    value: res.data?.insuranceUserResponse?.roleId,
                    label:
                      res?.data?.insuranceUserResponse?.roleTitle ||
                      res.data?.insuranceUserResponse?.roleId,
                  },
                  username:
                    res?.data?.insuranceUserResponse?.autoDetails === 2 ||
                    res?.data?.insuranceUserResponse?.autoDetails === 10 ||
                    res?.data?.insuranceUserResponse?.autoDetails === 11 ||
                    res?.data?.insuranceUserResponse?.autoDetails === 12 ||
                    res?.data?.insuranceUserResponse?.autoDetails === 20 ||
                    res?.data?.insuranceUserResponse?.autoDetails === 21
                      ? ""
                      : res?.data?.insuranceUserResponse?.username,
                }
              : null,
          });
          setUsername(res?.data?.insuranceUserResponse?.username || "");
          captureLabel(res?.data?.title);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    const addressUser = { ...data.addressesRequest };
    const isEmptyObject = (obj: any) =>
      !obj ||
      Object.values(obj).every(
        (v) => v === "" || v === null || v === undefined,
      );
    if (companyId) {
      delete data?.addressesResponse;
      InsuranceProfileService.updateInsuranceCompany(Number(companyId), {
        ...data,
        priceListId: data?.priceListId?.value || null,
        invoicingPreference: data?.invoicingPreference?.value
          ? data?.invoicingPreference.value
          : null,
        insuranceUserRequest: {
          ...data.insuranceUserRequest,
          roleId: data?.insuranceUserRequest?.roleId?.value || null,
          email: data?.insuranceUserRequest?.username
            ? data?.insuranceUserRequest?.username || username
            : null,
          username: data?.insuranceUserRequest?.username
            ? data?.insuranceUserRequest?.username || username
            : null,
        },
        addressesRequest: isEmptyObject(addressUser)
          ? null
          : {
              ...data?.addressesRequest,
              id: data?.addressesRequest?.id || null,
            },
      })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            navigate(-1);
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("insuranceCompany") +
                  " " +
                  t("updated") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      delete data.insuranceUserRequest.showConfirmPassword;
      delete data.insuranceUserRequest.showPassword;
      delete data.insuranceUserRequest.confirmPassword;
      InsuranceProfileService.createInsuranceCompany({
        ...data,
        priceListId: data?.priceListId?.value || null,
        invoicingPreference: data?.invoicingPreference?.value
          ? data?.invoicingPreference.value
          : null,
        active: true,
        insuranceUserRequest: {
          ...data?.insuranceUserRequest,
          roleId: data?.insuranceUserRequest?.roleId?.value || null,
          email: data?.insuranceUserRequest?.username || null,
        },
        addressesRequest: isEmptyObject(addressUser)
          ? null
          : {
              ...data?.addressesRequest,
            },
      })
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("insuranceCompany") +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "roles")
        rolesRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "priceLists")
        priceListRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={
          companyId ? t("editInsuranceCompany") : t("addInsuranceCompany")
        }
        icon={
          <AssuredWorkloadOutlinedIcon
            sx={{ fill: "#000", width: "30px", height: "30px" }}
          />
        }
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_INSURANCECOMPANY_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      <Box className={`${companyId ? "flex" : ""}`}>
        {companyId ? <InsuranceTabs params={params} selectedIndex={0} /> : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            padding={false}
            title={
              (params?.id ? t("editCustomer") : t("addCustomer")) +
              " (" +
              t("company") +
              ")"
            }
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <Grid
                container
                component="form"
                id="form"
                className="items-start mb-2 px-3"
                spacing={2}
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                  "& .MuiStep-horizontal .Mui-active + .MuiStepLabel-labelContainer":
                    {
                      background: `linear-gradient(20.78deg, ${themeColors?.secondary}, ${themeColors?.secondary + "B3"})`,
                    },
                }}
              >
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12}>
                    <h1
                      className="flex font-Saira font-[700] text-[17px]"
                      style={{ color: themeColors?.primary }}
                    >
                      {t("companyDetails")}
                    </h1>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("title", {
                        validate: {
                          required: (value) =>
                            value?.trim() !== "" ||
                            t("This field is required."),
                        },
                      })}
                      label={`${t("company")} ${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`}
                      placeholder={localizedInsuranceCompanyPlaceholder(
                        primaryLanguage,
                      )}
                      title={`${t("company")} ${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`
                        .replace(/\s+/g, " ")
                        .trim()}
                      fullWidth
                      error={!!errors?.title}
                      helperText={errors?.title?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("secondaryTitle")}
                      label={`${t("company")} ${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`}
                      placeholder={localizedInsuranceCompanyPlaceholder(
                        secondaryLanguage,
                      )}
                      title={`${t("company")} ${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`
                        .replace(/\s+/g, " ")
                        .trim()}
                      fullWidth
                      error={!!errors?.secondaryTitle}
                      helperText={errors?.secondaryTitle?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="text"
                      {...register("phone", {
                        validate: {
                          phoneLength: (value) =>
                            value === "" ||
                            value === null ||
                            (value?.length >= 5 && value?.length <= 17) ||
                            t("phoneLength"),
                        },
                      })}
                      label={t("phoneNumber")}
                      placeholder={t("phCompanyPhone")}
                      title={t("phoneNumber")}
                      fullWidth
                      error={!!errors?.phone}
                      helperText={errors?.phone?.message as string}
                      InputLabelProps={{ shrink: true }}
                      onChange={(
                        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                      ) => {
                        const cleaned = e.target.value.replace(/[^0-9+]/g, "");
                        setValue("phone", cleaned);
                        if (!IsBtnEnabled) setIsBtnEnabled(true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("taxNumber")}
                      label={t("taxNumber")}
                      placeholder={t("phInsuranceTaxNumber")}
                      title={t("taxNumber")}
                      fullWidth
                      error={!!errors?.taxNumber}
                      helperText={errors?.taxNumber?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="email"
                      {...register("email", {
                        required: t("This field is required."),
                        pattern: {
                          value:
                            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: t("validEmail"),
                        },
                      })}
                      label={t("email") + " *"}
                      placeholder={t("phCompanyEmail")}
                      title={t("email")}
                      fullWidth
                      error={!!errors?.email}
                      helperText={errors?.email?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="email"
                      {...register("secondaryEmail", {
                        required: false,
                        pattern: {
                          value:
                            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: t("validEmail"),
                        },
                      })}
                      label={t("secondaryEmail")}
                      placeholder={t("phSecondaryEmailOptional")}
                      title={t("secondaryEmail")}
                      fullWidth
                      error={!!errors?.secondaryEmail}
                      helperText={errors?.secondaryEmail?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("tradeLicenseNumber", {
                        validate: {
                          required: (value) =>
                            value.trim() !== "" || t("This field is required."),
                        },
                      })}
                      label={t("tradeLicenseNumber") + " *"}
                      InputLabelProps={{ shrink: true }}
                      placeholder={t("phTradeLicenseNumber")}
                      title={t("tradeLicenseNumber")}
                      fullWidth
                      error={!!errors.tradeLicenseNumber}
                      helperText={errors.tradeLicenseNumber?.message as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("website")}
                      label={t("website")}
                      placeholder={t("phCompanyWebsite")}
                      title={t("website")}
                      fullWidth
                      error={!!errors.website}
                      helperText={errors.website?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("issuingState")}
                      label={t("issuingState")}
                      placeholder={t("phIssuingState")}
                      title={t("issuingState")}
                      fullWidth
                      error={!!errors.issuingState}
                      helperText={errors.issuingState?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="dateOfIncorporation"
                      control={control}
                      rules={{
                        validate: (value) => {
                          const date = new Date(value);
                          return isNaN(date.getTime())
                            ? t("Invalid Date")
                            : true;
                        },
                      }}
                      render={({ field }) => (
                        <BasicDatePicker
                          type="datePicker"
                          {...field}
                          label={t("dateOfIncorporation") + " *"}
                          fullWidth
                          onChange={(value: any) =>
                            field.onChange(
                              new Date(value)
                                .toLocaleDateString("en-CA", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                })
                                ?.split("/")
                                .reverse()
                                .join("-"),
                            )
                          }
                          required={false}
                          error={errors.dateOfIncorporation?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="priceListId"
                      control={control}
                      defaultValue={null}
                      rules={{ required: t("This field is required.") }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("priceList") + " *"}
                          defaultPlaceholder="phSelectApplicablePriceList"
                          {...field}
                          options={priceLists}
                          isSearchable
                          onChange={(selectedOption: any) =>
                            field.onChange(selectedOption)
                          }
                          error={errors?.priceListId?.message as string}
                          isLoading={searchLoading === "priceLists"}
                          onInputChange={(
                            search: string,
                            { action }: { action: string },
                          ) => {
                            if (
                              action === "input-change" ||
                              (action !== "set-value" &&
                                action !== "menu-close" &&
                                action !== "input-blur")
                            )
                              handleDropdownActions(
                                "priceLists",
                                "searchIt",
                                search,
                              );
                            else if (
                              action === "input-blur" ||
                              action === "menu-close"
                            ) {
                              if (dropdownSearchTerm || pageNo > 1)
                                handleDropdownActions(
                                  "priceLists",
                                  "searchIt",
                                  "",
                                );
                              else setDropdownSearchTerm("");
                              setPageNo(1);
                            }
                          }}
                          onMenuScrollToBottom={() => {
                            if (!(priceListData?.pages[0]?.data as any).last)
                              handleDropdownActions("priceLists", "loadNext");
                          }}
                          page={pageNo}
                        />
                      )}
                    />
                  </Grid>
                  {/* <Grid item xs={12} md={3}>
                                    <Controller
                                        name="invoicingPreference"
                                        control={control}
                                        defaultValue={null}
                                        rules={{ required: false }}
                                        render={({ field }) => (
                                            <DynamicSelect
                                                label={t("invoicingPreference")}
                                                {...field}
                                                options={invoicingPreferences}
                                                isSearchable
                                                onChange={(selectedOption: any) => field.onChange(selectedOption)}
                                                error={errors?.invoicingPreference?.message as string}
                                            />
                                        )}
                                    />
                                </Grid> */}
                </Grid>
                <Grid item container xs={12} spacing={2} className="mt-[0px]">
                  <Accordion
                    defaultExpanded
                    sx={{
                      boxShadow: "none !important",
                      width: "100% !important",
                      padding: "7px 12px 12px 0",
                      borderRadius: "8px !important",
                      borderTop: "4px solid #fff",
                      "&.Mui-expanded": { borderColor: themeColors?.primary },
                      "& .MuiAccordionSummary-root": {
                        minHeight: "unset !important",
                        paddingRight: 0,
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Box
                          sx={{
                            background: "#E1E8E3",
                            borderRadius: "8px",
                            padding: "6px",
                          }}
                        >
                          <ExpandMoreIcon
                            sx={{
                              stroke: themeColors?.primary,
                              strokeWidth: "0.3",
                            }}
                          />
                        </Box>
                      }
                      aria-controls="address-content"
                      id="address-header"
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          margin: "0 !important",
                        },
                      }}
                    >
                      <h1
                        className="flex font-Saira font-[600] text-[14px]"
                        style={{ color: themeColors?.primary }}
                      >
                        {!primaryLanguage ? t("primary") : ""} {t("address")}{" "}
                        {primaryLanguage ? ` (${t(primaryLanguage)})` : ""}
                      </h1>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid item container spacing={2} xs={12}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            {...register("addressesRequest.address")}
                            label={t("address")}
                            placeholder={t("phStreetAddress")}
                            title={t("address")}
                            fullWidth
                            error={!!(errors.addressesRequest as any)?.address}
                            helperText={
                              (errors.addressesRequest as any)?.address
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register("addressesRequest.buildingNumber")}
                            label={t("buildingNumber")}
                            placeholder={t("phBuildingNumber")}
                            title={t("buildingNumber")}
                            fullWidth
                            error={
                              !!(errors.addressesRequest as any)?.buildingNumber
                            }
                            helperText={
                              (errors.addressesRequest as any)?.buildingNumber
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register("addressesRequest.street")}
                            label={t("street")}
                            placeholder={t("phAlWaslStreet")}
                            title={t("street")}
                            fullWidth
                            error={!!(errors.addressesRequest as any)?.street}
                            helperText={
                              (errors.addressesRequest as any)?.street
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register("addressesRequest.district")}
                            label={t("district")}
                            placeholder={t("phDistrict")}
                            title={t("district")}
                            fullWidth
                            error={!!(errors.addressesRequest as any)?.district}
                            helperText={
                              (errors.addressesRequest as any)?.district
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register("addressesRequest.city")}
                            label={t("city")}
                            placeholder={t("phCity")}
                            title={t("city")}
                            fullWidth
                            error={!!(errors.addressesRequest as any)?.city}
                            helperText={
                              (errors.addressesRequest as any)?.city
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register("addressesRequest.country")}
                            label={t("country")}
                            placeholder={t("phCountryUAE")}
                            title={t("country")}
                            fullWidth
                            error={!!(errors.addressesRequest as any)?.country}
                            helperText={
                              (errors.addressesRequest as any)?.country
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register("addressesRequest.postalCode")}
                            label={t("postalCode")}
                            placeholder={t("phPostalCodeZero")}
                            title={t("postalCode")}
                            fullWidth
                            error={
                              !!(errors.addressesRequest as any)?.postalCode
                            }
                            helperText={
                              (errors.addressesRequest as any)?.postalCode
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                <Grid item container xs={12} spacing={2} className="mt-[0px]">
                  <Accordion
                    sx={{
                      boxShadow: "none !important",
                      width: "100% !important",
                      padding: "7px 12px 12px 0",
                      borderRadius: "8px !important",
                      borderTop: "4px solid #fff",
                      "&.Mui-expanded": { borderColor: themeColors?.primary },
                      "& .MuiAccordionSummary-root": {
                        minHeight: "unset !important",
                        paddingRight: 0,
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Box
                          sx={{
                            background: "#E1E8E3",
                            borderRadius: "8px",
                            padding: "6px",
                          }}
                        >
                          <ExpandMoreIcon
                            sx={{
                              stroke: themeColors?.primary,
                              strokeWidth: "0.3",
                            }}
                          />
                        </Box>
                      }
                      aria-controls="address-content"
                      id="address-header"
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          margin: "0 !important",
                        },
                      }}
                    >
                      <h1
                        className="flex font-Saira font-[600] text-[14px]"
                        style={{ color: themeColors?.primary }}
                      >
                        {!secondaryLanguage ? t("secondary") : ""}{" "}
                        {t("address")}{" "}
                        {secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}
                      </h1>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid item container spacing={2} xs={12}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            {...register("addressesRequest.secondaryAddress")}
                            label={t("address")}
                            placeholder={t("phStreetAddress")}
                            title={t("address")}
                            fullWidth
                            error={
                              !!(errors.addressesRequest as any)
                                ?.secondaryAddress
                            }
                            helperText={
                              (errors.addressesRequest as any)?.secondaryAddress
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register(
                              "addressesRequest.secondaryBuildingNumber",
                            )}
                            label={t("buildingNumber")}
                            placeholder={t("phBuildingNumber")}
                            title={t("buildingNumber")}
                            fullWidth
                            error={
                              !!(errors.addressesRequest as any)
                                ?.secondaryBuildingNumber
                            }
                            helperText={
                              (errors.addressesRequest as any)
                                ?.secondaryBuildingNumber?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register("addressesRequest.secondaryStreet")}
                            label={t("street")}
                            placeholder={t("phAlWaslStreet")}
                            title={t("street")}
                            fullWidth
                            error={
                              !!(errors.addressesRequest as any)
                                ?.secondaryStreet
                            }
                            helperText={
                              (errors.addressesRequest as any)?.secondaryStreet
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register("addressesRequest.secondaryDistrict")}
                            label={t("district")}
                            placeholder={t("phDistrict")}
                            title={t("district")}
                            fullWidth
                            error={
                              !!(errors.addressesRequest as any)
                                ?.secondaryDistrict
                            }
                            helperText={
                              (errors.addressesRequest as any)
                                ?.secondaryDistrict?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register("addressesRequest.secondaryCity")}
                            label={t("city")}
                            placeholder={t("phCity")}
                            title={t("city")}
                            fullWidth
                            error={
                              !!(errors.addressesRequest as any)?.secondaryCity
                            }
                            helperText={
                              (errors.addressesRequest as any)?.secondaryCity
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register("addressesRequest.secondaryCountry")}
                            label={t("country")}
                            placeholder={t("phCountryUAE")}
                            title={t("country")}
                            fullWidth
                            error={
                              !!(errors.addressesRequest as any)
                                ?.secondaryCountry
                            }
                            helperText={
                              (errors.addressesRequest as any)?.secondaryCountry
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            {...register(
                              "addressesRequest.secondaryPostalCode",
                            )}
                            label={t("postalCode")}
                            placeholder={t("phPostalCodeZero")}
                            title={t("postalCode")}
                            fullWidth
                            error={
                              !!(errors.addressesRequest as any)
                                ?.secondaryPostalCode
                            }
                            helperText={
                              (errors.addressesRequest as any)
                                ?.secondaryPostalCode?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                <Grid item xs={12}>
                  <h1
                    className="flex font-Saira font-[700] text-[17px]"
                    style={{ color: themeColors?.primary }}
                  >
                    {t("userDetails")}
                  </h1>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("insuranceUserRequest.name", {
                      validate: {
                        required: (value) =>
                          value?.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={`${t("user")} ${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={localizedContactPersonPlaceholder(
                      primaryLanguage,
                    )}
                    title={`${t("user")} ${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!(errors as any)?.insuranceUserRequest?.name}
                    helperText={
                      (errors as any)?.insuranceUserRequest?.name
                        ?.message as string
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("insuranceUserRequest.secondaryName")}
                    label={`${t("user")} ${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={localizedContactPersonPlaceholder(
                      secondaryLanguage,
                    )}
                    title={`${t("user")} ${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={
                      !!(errors as any)?.insuranceUserRequest?.secondaryName
                    }
                    helperText={
                      (errors as any)?.insuranceUserRequest?.secondaryName
                        ?.message as string
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="text"
                    {...register("insuranceUserRequest.phone", {
                      validate: {
                        phoneLength: (value) =>
                          value === "" ||
                          value === null ||
                          (value?.length >= 5 && value?.length <= 17) ||
                          t("phoneLength"),
                      },
                    })}
                    label={t("phoneNumber")}
                    placeholder={t("phContactPhone")}
                    title={t("phoneNumber")}
                    fullWidth
                    error={!!(errors as any)?.insuranceUserRequest?.phone}
                    helperText={
                      (errors as any)?.insuranceUserRequest?.phone
                        ?.message as string
                    }
                    InputLabelProps={{ shrink: true }}
                    onChange={(
                      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                    ) => {
                      const cleaned = e.target.value.replace(/[^0-9+]/g, "");
                      setValue("insuranceUserRequest.phone", cleaned);
                      if (!IsBtnEnabled) setIsBtnEnabled(true);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("insuranceUserRequest.address")}
                    label={t("address")}
                    placeholder={t("phContactAddress")}
                    title={t("address")}
                    fullWidth
                    error={!!(errors as any)?.insuranceUserRequest?.address}
                    helperText={
                      (errors as any)?.insuranceUserRequest?.address
                        ?.message as string
                    }
                    InputLabelProps={{ shrink: true }}
                    multiline
                    rows={1}
                    sx={{
                      "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                        width: "100%",
                        paddingRight: "0px",
                        paddingBottom: 0,
                      },
                      "& .MuiInputBase-inputMultiline": {
                        resize: "vertical",
                        minHeight: "29px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="insuranceUserRequest.roleId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("role") + " *"}
                        defaultPlaceholder="phSelectRole"
                        {...field}
                        options={roles}
                        isSearchable
                        // isDisabled={params?.id ? true : false}
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={
                          (errors as any)?.insuranceUserRequest?.roleId
                            ?.message as string
                        }
                        isLoading={searchLoading === "roles"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions("roles", "searchIt", search);
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || pageNo > 1)
                              handleDropdownActions("roles", "searchIt", "");
                            else setDropdownSearchTerm("");
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (!(rolesData?.pages[0]?.data as any).last)
                            handleDropdownActions("roles", "loadNext");
                        }}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="insuranceUserRequest.loginAccess"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <div>
                        <CommonCheckbox
                          {...field}
                          label={t("loginAccess")}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            field.onChange(event.target.checked);
                            if (!event.target.checked) {
                              setValue("insuranceUserRequest.username", "");
                              setValue("insuranceUserRequest.password", "");
                              setValue(
                                "insuranceUserRequest.confirmPassword",
                                "",
                              );
                            }
                          }}
                          checked={field.value}
                          color={themeColors?.primary}
                        />
                      </div>
                    )}
                  />
                </Grid>
                {watch("insuranceUserRequest.loginAccess") ? (
                  <>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="email"
                        {...register("insuranceUserRequest.username", {
                          required: watch("insuranceUserRequest.loginAccess")
                            ? t("This field is required.")
                            : false,
                          pattern: {
                            value:
                              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: t("validEmail"),
                          },
                        })}
                        label={
                          t("email") +
                          (watch("insuranceUserRequest.loginAccess")
                            ? " *"
                            : "")
                        }
                        placeholder={
                          t("email") +
                          (watch("insuranceUserRequest.loginAccess")
                            ? " *"
                            : "")
                        }
                        title={
                          t("email") +
                          (watch("insuranceUserRequest.loginAccess")
                            ? " *"
                            : "")
                        }
                        fullWidth
                        error={
                          !!(errors as any)?.insuranceUserRequest?.username
                        }
                        helperText={
                          (errors as any)?.insuranceUserRequest?.username
                            ?.message as string
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    {params?.id ? null : (
                      <>
                        <Grid item xs={12} md={3}>
                          <TextField
                            type={
                              watch("insuranceUserRequest.showPassword")
                                ? "text"
                                : "password"
                            }
                            {...register("insuranceUserRequest.password", {
                              required: watch(
                                "insuranceUserRequest.loginAccess",
                              )
                                ? t("This field is required.")
                                : false,
                              validate:
                                watch("insuranceUserRequest.loginAccess") ||
                                watch("insuranceUserRequest.password")?.length
                                  ? {
                                      passwordLength: (value) =>
                                        value?.length >= 8 ||
                                        t("minPasswordLength"),
                                    }
                                  : undefined,
                            })}
                            label={
                              t("password") +
                              (watch("insuranceUserRequest.loginAccess")
                                ? " *"
                                : "")
                            }
                            placeholder={
                              t("password") +
                              (watch("insuranceUserRequest.loginAccess")
                                ? " *"
                                : "")
                            }
                            title={
                              t("password") +
                              (watch("insuranceUserRequest.loginAccess")
                                ? " *"
                                : "")
                            }
                            fullWidth
                            error={
                              !!(errors as any)?.insuranceUserRequest?.password
                            }
                            helperText={
                              (errors as any)?.insuranceUserRequest?.password
                                ?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              endAdornment: watch(
                                "insuranceUserRequest.password",
                              ) ? (
                                <InputAdornment
                                  position="end"
                                  className="cursor-pointer"
                                >
                                  {watch(
                                    "insuranceUserRequest.showPassword",
                                  ) ? (
                                    <VisibilityIcon
                                      style={{ color: themeColors?.primary }}
                                      onClick={() =>
                                        setValue(
                                          "insuranceUserRequest.showPassword",
                                          false,
                                        )
                                      }
                                    />
                                  ) : (
                                    <VisibilityOffIcon
                                      style={{ color: themeColors?.primary }}
                                      onClick={() =>
                                        setValue(
                                          "insuranceUserRequest.showPassword",
                                          true,
                                        )
                                      }
                                    />
                                  )}
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            type={
                              watch("insuranceUserRequest.showConfirmPassword")
                                ? "text"
                                : "password"
                            }
                            {...register(
                              "insuranceUserRequest.confirmPassword",
                              {
                                required: watch(
                                  "insuranceUserRequest.loginAccess",
                                )
                                  ? t("This field is required.")
                                  : false,
                                validate:
                                  watch("insuranceUserRequest.loginAccess") ||
                                  watch("insuranceUserRequest.password")
                                    ?.length ||
                                  watch("insuranceUserRequest.confirmPassword")
                                    ?.length
                                    ? {
                                        passwordMatch: (value) =>
                                          value ===
                                            watch(
                                              "insuranceUserRequest.password",
                                            ) ||
                                          t("confirmPasswordNewPassword"),
                                      }
                                    : undefined,
                              },
                            )}
                            label={
                              t("confirmNewPassword") +
                              (watch("insuranceUserRequest.loginAccess")
                                ? " *"
                                : "")
                            }
                            placeholder={
                              t("confirmNewPassword") +
                              (watch("insuranceUserRequest.loginAccess")
                                ? " *"
                                : "")
                            }
                            title={
                              t("confirmNewPassword") +
                              (watch("insuranceUserRequest.loginAccess")
                                ? " *"
                                : "")
                            }
                            fullWidth
                            error={
                              !!(errors as any)?.insuranceUserRequest
                                ?.confirmPassword
                            }
                            helperText={
                              (errors as any)?.insuranceUserRequest
                                ?.confirmPassword?.message as string
                            }
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              endAdornment: watch(
                                "insuranceUserRequest.confirmPassword",
                              ) ? (
                                <InputAdornment
                                  position="end"
                                  className="cursor-pointer"
                                >
                                  {watch(
                                    "insuranceUserRequest.showConfirmPassword",
                                  ) ? (
                                    <VisibilityIcon
                                      style={{ color: themeColors?.primary }}
                                      onClick={() =>
                                        setValue(
                                          "insuranceUserRequest.showConfirmPassword",
                                          false,
                                        )
                                      }
                                    />
                                  ) : (
                                    <VisibilityOffIcon
                                      style={{ color: themeColors?.primary }}
                                      onClick={() =>
                                        setValue(
                                          "insuranceUserRequest.showConfirmPassword",
                                          true,
                                        )
                                      }
                                    />
                                  )}
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                ) : null}
              </Grid>
            }
          />
        )}
      </Box>
    </>
  );
}
